import { collectAllPages, parseAwsJson } from "@examspark/common";
import {
  Expand,
  ParsedAppUserConfig,
  App,
  AppConfig,
  AppKey,
  AppLicense,
  AuthorizationContextInput,
  ModelResourceConnection,
  Resource,
  ParsedApp,
  RegisterContentResponse,
  ListAppUserConfigsInput,
  ModelAppUserConnection,
  CreateAppUserConfigInput,
  IDSK,
  ContentItemSelectionParams,
  AppDomain,
  lti1p3DeepLinkingResponseSchema,
} from "@examspark/types";

import { IdentityContextProvider } from "../../identitycontext";
import { GraphQLAPI } from "../client";
import { parseResource } from "../deserializers";
import * as fragments from "../fragments/resource";
import { app } from "../queries";
import { ResourceIdFields, ResourceTimestampFields } from "../types";
import { typedGql } from "../utils";

import {
  listResources,
  listResourcesBySkThenId,
  listResourcesByWorkspaceId,
  updateResource,
} from "./resource";

export * from "./app.registration";

const fragments2 = {
  id: `
    id
    sk
    idInc
    idVendor
    schemaVersion
  `,
  timestamps: `
    createdAt
    updatedAt
  `,
  app: `
    appId
    bankId
    description
    name
    resourceId
    type
    version
    workspaceId
    config
  `,
  appConfig: `
    config
  `,
  appKey: `
    appKey
    appSecret
  `,
  appUserConfig: `
    id
    sk
    schemaVersion
    createdAt
    updatedAt
    config
    workspaceId
  `,
};

export const getAppKey = async (appKey: string) => {
  const result = await GraphQLAPI.client.query<{
    getResource: QueryGetAppFields;
  }>({
    query: /* GraphQL */ `
      query GetAppKey($id: ID!, $sk: String!) {
        getResource(id: $id, sk: $sk) {
          ${fragments2.id}
          ${fragments2.timestamps}
          ${fragments2.app}
        }
      }
    `,
    variables: {
      id: appKey,
      sk: "app-key",
    },
  });

  return result.data?.getResource as AppKey;
};

export const getAppDomain = async (domain: string) => {
  const result = await GraphQLAPI.client.query<{
    getAppDomain: Resource;
  }>({
    query: /* GraphQL */ `
      query GetAppDomain($input: ModelGetInput!) {
        getAppDomain(input: $input) {
          id
          sk
          workspaceId
          app {
            id
            sk
            name
            workspaceId
            config
            workspaceId
          }
        }
      }
    `,
    variables: {
      input: {
        id: domain,
        sk: "app-domain",
      },
    },
  });

  return result.data?.getAppDomain as AppDomain;
};

export const getAppKeyCredentials = async (appKey: string) => {
  const result = await GraphQLAPI.client.query<{
    getResource: QueryGetAppKeyCredentials;
  }>({
    query: /* GraphQL */ `
      query GetAppKey($id: ID!, $sk: String!) {
        getResource(id: $id, sk: $sk) {
          ${fragments2.id}
          ${fragments2.timestamps}
          ${fragments2.app}
          ${fragments2.appKey}
          resource {
            id
            sk
            name
            description
            ${fragments2.appConfig}
          }
        }
      }
    `,
    variables: {
      id: appKey,
      sk: "app-key",
    },
  });

  return result.data?.getResource;
};

export type QueryGetAppFields = Expand<
  Required<
    ResourceIdFields &
      ResourceTimestampFields &
      Pick<
        Resource,
        | "appId"
        | "bankId"
        | "description"
        | "name"
        | "resourceId"
        | "type"
        | "version"
        | "workspaceId"
        | "config"
      >
  >
>;

export type QueryGetAppKeyCredentials = Expand<
  Required<
    ResourceIdFields &
      ResourceTimestampFields &
      Pick<
        Resource,
        | "appId"
        | "appKey"
        | "appSecret"
        | "bankId"
        | "description"
        | "name"
        | "resourceId"
        | "type"
        | "version"
        | "workspaceId"
      > & {
        resource?: Pick<Resource, "id" | "sk" | "name" | "description"> & {
          config?: AppConfig;
        };
      }
  >
>;

export const getApp = async (appId: string) => {
  const result = await GraphQLAPI.client.query<{
    getResource: App & {
      workspace: Resource["workspace"];
    };
  }>({
    authMode: "API_KEY",
    query: /* GraphQL */ `
      query GetApp($id: ID!, $sk: String!) {
        getResource(id: $id, sk: $sk) {
          ${fragments2.id}
          ${fragments2.timestamps}
          ${fragments2.app}
          workspace {
            id
            sk
            name
          }
        }
      }
    `,
    variables: {
      id: appId,
      sk: "app",
    },
  });

  const app = result.data?.getResource;
  const parsedApp = parseResource(app, (r) => ({
    config: parseAwsJson(r.config),
  })) as ParsedApp & {
    workspace: Resource["workspace"];
  };

  return parsedApp;
};

export const getAppDetails = async (appId: string) => {
  const result = await GraphQLAPI.client.query<{ getResource: App }>({
    query: /* GraphQL */ `
      query GetApp($id: ID!, $sk: String!) {
        getResource(id: $id, sk: $sk) {
          ${fragments2.id}
          ${fragments2.timestamps}
          ${fragments2.app}
          config
        }
      }
    `,
    variables: {
      id: appId,
      sk: "app",
    },
  });

  const item = result.data?.getResource;
  if (item?.config && typeof item.config === "string") {
    item.config = JSON.parse(item.config);
  }

  return item;
};

export const getAppLicense = async (appId: string, workspaceId: string) => {
  const result = await GraphQLAPI.client.query<{
    getResource: Expand<
      ResourceIdFields &
        Required<Pick<AppLicense, "appId" | "appKey" | "workspaceId" | "tags">>
    >;
  }>({
    query: /* GraphQL */ `
      query GetAppLicense($id: ID!, $sk: String!) {
        getResource(id: $id, sk: $sk) {
          ${fragments2.id}
          appId
          appKey
          workspaceId
          tags {
            key
            type
            value
          }
        }
      }
    `,
    variables: {
      id: appId.trim(),
      sk: `app-license#${workspaceId.trim()}`,
    },
  });

  return result.data?.getResource;
};

export type QueryGetUserConfigFields = Expand<
  Required<
    ResourceIdFields &
      ResourceTimestampFields &
      Pick<Resource, "config" | "workspaceId">
  >
>;

/** Return `app-user-config` DDB item with `config` property unwrapped.*/
export const getUserConfig = async (appKeyId: string, userId: string) => {
  const result = await GraphQLAPI.client.query<{
    getResource: QueryGetUserConfigFields;
  }>({
    query: /* GraphQL */ `
      query GetAppUserConfig($id: ID!, $sk: String!) {
        getResource(id: $id, sk: $sk) {
          ${fragments2.appUserConfig}
        }
      }
    `,
    variables: {
      id: appKeyId,
      sk: ["app-user-config", userId].join("#"),
    },
  });

  if (!result.data?.getResource) return undefined;
  const { config, workspaceId, ...item } = result.data.getResource;
  if (workspaceId == null) return undefined;

  const next: ParsedAppUserConfig = {
    ...item,
    workspaceId,
    config: parseAwsJson(config),
  };

  return next;
};

/** Creates or updates a user's app-user-config record. */
export const setUserConfig = async (
  appKeyId: string,
  userId: string,
  config: any,
) => {
  /** @todo create a proper ExamConfig data type */
  const input: Resource = {
    id: appKeyId,
    sk: ["app-user-config", userId].join("#"),
    schemaVersion: "2020-01-01",
    config: JSON.stringify(config),
  };

  const existing = await getUserConfig(appKeyId, userId);
  if (existing) {
    input.config = JSON.stringify({
      ...existing.config,
      ...config,
    });

    const result = await GraphQLAPI.client.query<{
      updateResource: QueryGetUserConfigFields;
    }>({
      query: /* GraphQL */ `
        mutation UpdateUserConfig($input: UpdateResourceInput!) {
          updateResource(input: $input) {
            ${fragments2.appUserConfig}
          }
        }
      `,
      variables: {
        input,
      },
    });

    return result.data?.updateResource;
  } else {
    const appKeyItem = await getAppKey(appKeyId);
    if (!appKeyItem?.workspaceId) return;

    const result = await GraphQLAPI.client.query<{ createResource: Resource }>({
      query: /* GraphQL */ `
        mutation UpdateUserConfig($input: CreateResourceInput!) {
          createResource(input: $input) {
            ${fragments2.appUserConfig}
          }
        }
      `,
      variables: {
        input: {
          ...input,
          workspaceId: appKeyItem.workspaceId,
        },
      },
    });

    return result.data?.createResource;
  }
};

export const listApplications = async (params?: { limit?: number }) => {
  return await listResourcesBySkThenId({
    label: "ListApps",
    variables: {
      ...params,
      sk: "app",
    },
    fields: `
      items {
        ${fragments.id}
        ${fragments.basic}
      }
    `,
  });
};

export const listAppLicenses = async (
  params: {
    id: string;
    limit?: number;
  },
  options?: {
    fields?: string;
  },
) => {
  const licenses: Resource[] = [];

  const fetchLicenses = async (nextToken?: string) => {
    const result = await listResources({
      label: "ListAppLicenses",
      variables: {
        ...params,
        nextToken,
        sk: {
          beginsWith: "app-license#",
        },
      },
      fields:
        options?.fields ??
        `
        items {
          ${fragments.id}
          ${fragments.basic}
          appKey
          workspace {
            id
            sk
            name
          }
        }
        nextToken
      `,
    });

    if (result?.items?.length) {
      for (const item of result.items) {
        if (!item) continue;
        licenses.push(item);
      }
    }

    if (result?.nextToken) {
      await fetchLicenses(result.nextToken);
    }
  };

  await fetchLicenses();
  return licenses;
};

export const listAppUserConfigs = async (appKeyId: string | undefined) => {
  if (appKeyId == null) return [];

  const allPages = await collectAllPages(async (nextToken) => {
    const input: ListAppUserConfigsInput = {
      appKeyId,
      nextToken,
    };

    const page = await GraphQLAPI.query<{
      listAppUserConfigs: ModelAppUserConnection;
    }>({
      query: /* GraphQL */ `
      query ListAppUserConfigs($input: ListAppUserConfigsInput!, $authorizationContext: AuthorizationContextInput!) {
        listAppUserConfigs(input: $input, authorizationContext: $authorizationContext) {
          items {
            ${fragments2.appUserConfig}
            workspaceUser {
              id
              sk
              workspaceUserId
              user {
                id
                sk
                firstName
                lastName
                email
              }
            }
          }
          nextToken
        }
      }
    `,
      variables: {
        input,
        authorizationContext:
          IdentityContextProvider.getContext() as AuthorizationContextInput,
      },
    });

    return page.data?.listAppUserConfigs;
  });

  return allPages
    .flatMap((page) => page.items ?? [])
    .map(
      (item) =>
        parseResource(item, (r) => ({
          config: parseAwsJson(r.config),
        })) as ParsedAppUserConfig,
    );
};

export const putAppUserConfig = async (input: CreateAppUserConfigInput) => {
  const result = await GraphQLAPI.query({
    query: /* GraphQL */ `
      mutation PutAppUserConfig(
        $input: CreateAppUserConfigInput!
        $authorizationContext: AuthorizationContextInput!
      ) {
        putAppUser(input: $input, authorizationContext: $authorizationContext) {
          id
          sk
        }
      }
    `,
    variables: {
      input: {
        id: input.id,
        sk: input.sk,
        schemaVersion: input.schemaVersion,
        workspaceId: input.workspaceId,
        config: JSON.stringify(input.config),
        createdAt: input.createdAt,
        updatedAt: input.updatedAt,
      } as CreateAppUserConfigInput,
      authorizationContext: IdentityContextProvider.getContext(),
    },
  });

  return result.data as IDSK;
};

export const listAppsByWorkspaceOwner = async (
  workspaceId: string,
  authorizationContext: AuthorizationContextInput,
) => {
  // todo: this on the server in its own resolver
  if (authorizationContext.workspaceId !== workspaceId) {
    throw new Error("Unauthorized");
  }

  return await listResourcesByWorkspaceId({
    label: "ListAppsByWorkspaceOwner",
    variables: {
      workspaceId,
      sk: {
        eq: `app`,
      },
    },
    fields: `
      items {
        ${fragments.id}
        ${fragments.basic}
      }
    `,
  });
};

export const listLicensedApps = async (workspaceId: string) => {
  const result = await GraphQLAPI.client.query<{
    listResourcesByWorkspaceId: ModelResourceConnection;
  }>({
    query: /* GraphQL */ `
      query ListLicensedApps(
        $workspaceId: String
        $sk: ModelStringKeyConditionInput
        $sortDirection: ModelSortDirection
        $filter: ModelResourceFilterInput
        $limit: Int
        $nextToken: String
      ) {
        listResourcesByWorkspaceId(
          workspaceId: $workspaceId
          sk: $sk
          sortDirection: $sortDirection
          filter: $filter
          limit: $limit
          nextToken: $nextToken
        ) {
          items {
            id
            sk
            name
            appKey
            createdAt
            updatedAt
            resource {
              id
              sk
              name
              config
              description
              workspace {
                name
              }
            }
          }
          nextToken
        }
      }
    `,
    variables: {
      workspaceId,
      sk: {
        beginsWith: "app-license#",
      },
    },
  });

  return result.data?.listResourcesByWorkspaceId;
};

export const disableAppLicense = async (appKeyId: string) => {
  return updateResource({
    id: appKeyId,
    sk: "app-key",
    isDisabled: true,
  });
};
export const enableAppLicense = async (appKeyId: string) => {
  return updateResource({
    id: appKeyId,
    sk: "app-key",
    isDisabled: false,
  });
};

export const signCampfireLti = async (
  ltiLaunchUrl: string,
  params: Record<string, string>,
  authorizationContext: AuthorizationContextInput,
) => {
  return typedGql(app.signCampfireLti)({
    input: {
      action: "signCampfireLti",
      params: JSON.stringify({
        ltiLaunchUrl,
        ltiParams: params,
      }),
    },
    authorizationContext,
  });
};

export const toggleAppLicense = async (
  workspaceId: string,
  appId: string,
  isDisabled: boolean,
) => {
  return GraphQLAPI.query<{
    toggleAppLicense: { signature: string };
  }>({
    query: app.toggleAppLicense,
    variables: {
      input: {
        action: "toggleAppLicense",
        params: JSON.stringify({
          workspaceId,
          appId,
          isDisabled,
        }),
      },
    },
  });
};

export const signContentItemSelectionLti = async (
  ltiContentItemReturnUrl: string,
  ltiParams: ContentItemSelectionParams,
  authorizationContext: AuthorizationContextInput,
) => {
  return GraphQLAPI.query<{
    signContentItemSelectionLti: { signature: string };
  }>({
    query: app.signContentItemSelectionLti,
    variables: {
      input: {
        action: "signContentItemSelectionLti",
        params: JSON.stringify({
          ltiContentItemReturnUrl,
          ltiParams,
        }),
      },
      authorizationContext,
    },
  });
};

export const signDeepLinkItemSelectionLti = async (
  params: Zod.infer<typeof lti1p3DeepLinkingResponseSchema>,
  authorizationContext: AuthorizationContextInput,
) => {
  return GraphQLAPI.query<{
    signContentItemSelectionLti: { signature: string };
  }>({
    query: app.signContentItemSelectionLti,
    variables: {
      input: {
        action: "signDeepLinkItemSelectionLti",
        params: JSON.stringify({
          ltiParams: params,
        }),
      },
      authorizationContext,
    },
  });
};

export const registerContent = async (
  code: string,
  authorizationContext: AuthorizationContextInput,
) => {
  return GraphQLAPI.query<{ registerContent: RegisterContentResponse }>({
    query: app.registerContent,
    variables: {
      input: {
        action: "registerContent",
        params: JSON.stringify({
          code,
        }),
      },
      authorizationContext,
    },
  });
};

export const deregisterContent = async (
  code: string,
  authorizationContext: AuthorizationContextInput,
) => {
  return GraphQLAPI.query<{ deregisterContent: RegisterContentResponse }>({
    query: app.deregisterContent,
    variables: {
      input: {
        action: "deregisterContent",
        params: JSON.stringify({
          code,
        }),
      },
      authorizationContext,
    },
  });
};
